import React, { FC, memo, MouseEvent, useMemo } from "react";
import { useDispatch } from "react-redux";

import { getLoopsField, getStopsField } from "Components/common/TableElements";
import { FMSLogger } from "FMSLogger";
import { QUEUE_MANAGER_ACTIONS } from "Utils/constants";
import {
  cancelLiveAndQueueMission,
  setHotJob,
  UNASSIGNED_MISSIONS_KEY,
} from "Slices/missionsPage";
import { UNCANCELABLE_CODES } from "Components/MissionPlanner/components/MissionPlannerLists";
import { toggleModal } from "Slices/modals";

import {
  CellButtonStyled,
  CellContainerStyled,
  HotJobButtonStyled,
  HotJobBadgeStyled,
  TableOptionsContainerStyled,
} from "./styles";
import { TableOptions } from "Components/NewList/TableOptions";
import { RowData } from "Components/NewList/types";

type MissionRowProps = {
  mission: Mission;
  vehicleId: string;
};

export const MissionRow: FC<MissionRowProps> = memo(
  ({ mission, vehicleId }) => {
    const dispatch = useDispatch();
    const { name, stops } = mission;

    const handleOpenMissionDetails = (event: MouseEvent<HTMLDivElement>) => {
      if (mission) {
        dispatch(
          toggleModal({
            type: "MISSION_DETAILS_MODAL",
            data: { missionId: mission.id },
          })
        );
        event.stopPropagation();
      }
    };

    const handleHotJob = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      dispatch(
        setHotJob({
          missionId: mission.id,
          isCurrentlyHot: !!mission.isHot,
        }) as any
      );
    };

    const queueManagerActionsLogger = FMSLogger.byPrefix(QUEUE_MANAGER_ACTIONS);

    const options = useMemo(
      () => [
        {
          name: "Cancel",
          dataTestId: "queue-manager-cancel-mission-button",
          // Hide Cancel for terminal/in-flight statuses — mirrors
          // MissionPlannerLists to keep the two cancel sites symmetric.
          isHide: () => UNCANCELABLE_CODES.includes(mission.status as string),
          callback: (item: RowData) => {
            queueManagerActionsLogger.debug(
              `Pressed cancel mission with id:${item.id}`
            );
            // Normalise the "unassigned" sentinel to null so the thunk
            // routes to the fleet-scoped cancel endpoint instead of building
            // an invalid URL like /vehicles/unassigned/mission_queue/{id}/cancel/.
            const resolvedVehicleId =
              vehicleId === UNASSIGNED_MISSIONS_KEY ? null : vehicleId;
            dispatch(
              cancelLiveAndQueueMission({
                missionId: item.id,
                vehicleId: resolvedVehicleId,
              })
            );
          },
        },
      ],
      [vehicleId, mission.status]
    );

    const optionsDropdownOffset = useMemo(() => ({ top: 50, right: -10 }), []);

    // Create a RowData-compatible object from MissionAsset
    const rowData: RowData = useMemo(
      () => ({
        id: mission.id,
        name: mission.name,
        stopsCount: stops.length.toString(),
        loopsCount: (mission.totalLoops || 1).toString(),
      }),
      [mission.id, mission.name, stops.length, mission.totalLoops]
    );

    return (
      <>
        <CellContainerStyled>
          <CellButtonStyled
            data-test-id={`queue-manager-mission-name-${mission.id}`}
            onClickCapture={(event) => handleOpenMissionDetails(event)}
          >
            {name}
          </CellButtonStyled>
          {!!mission.isHot && (
            <HotJobBadgeStyled
              data-test-id={`queue-manager-hot-job-badge-${mission.id}`}
            >
              HOT
            </HotJobBadgeStyled>
          )}
        </CellContainerStyled>
        <CellContainerStyled>
          {getStopsField(0, stops.length)}
        </CellContainerStyled>
        <CellContainerStyled>
          {getLoopsField(0, mission.totalLoops || 1)}
          <HotJobButtonStyled
            data-test-id={`queue-manager-hot-job-button-${mission.id}`}
            onClick={handleHotJob}
            isHot={!!mission.isHot}
            title="Mark as Hot Job"
            aria-label="Mark mission as hot job"
          >
            &#x1F4CC;
          </HotJobButtonStyled>
          <TableOptionsContainerStyled>
            <TableOptions
              item={rowData}
              options={options}
              optionsDropdownOffset={optionsDropdownOffset}
            />
          </TableOptionsContainerStyled>
        </CellContainerStyled>
      </>
    );
  }
);
